import { Image } from 'react-bootstrap';
import React, { memo, useState, useEffect, useContext } from 'react';
import styled from '@emotion/styled';
import { after } from '~/utils/Function';
import Select from '~/App/Components/Select';
import { Step } from '~/App/Components/Typeform';
import useRestApi from '~/react-hooks/useRestApi';
import ActionButton from '~/App/Components/Typeform/ActionButton';
import TypeformContext from '~/App/Components/Typeform/TypeformContext';
import type { UserSimple } from '~/types/models';

export type AssigneeProps = {
  assignee: UserSimple;
  updateAssignee: (assignee: UserSimple | null) => void;
};

const StyledSelect = styled(Select)<{ hasValidationError: boolean }>`
  > div {
    border-color: ${({ hasValidationError }) =>
      hasValidationError ? 'red' : '#cdcdcd'};
  }
`;

const Assignee = memo(({ assignee, updateAssignee }: AssigneeProps) => {
  const [input, setInput] = useState();
  const { scrollToNextStep } = useContext(TypeformContext);

  const [users, api, state, search] = useRestApi('/api/v2/users', {
    eagerLoad: true,
  });

  function onChange(option: UserSimple | null, { action }) {
    switch (action) {
      case 'select-option':
        updateAssignee(option);
        break;
      case 'clear':
        updateAssignee(null);
        break;
    }
  }

  function onInputChange(input, { action }) {
    switch (action) {
      case 'input-change':
        setInput(input);
        break;
    }
  }

  useEffect(() => {
    async function getCurrentUser() {
      const currentUser = await api.get('me');

      updateAssignee(currentUser);
    }

    getCurrentUser();
  }, []);

  useEffect(() => {
    search(input);
  }, [input]);

  return (
    <Step heading='Assign It To'>
      <StyledSelect
        required
        isLoading={state.isLoading}
        isClearable
        value={assignee}
        options={users.data}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => option.id}
        formatOptionLabel={(option) => (
          <div>
            <Image
              style={{ marginRight: 5 }}
              circle
              width={25}
              height={25}
              src={option.image}
            />
            {option.name}
          </div>
        )}
        onChange={after(onChange, (assignee) => {
          if (assignee) {
            scrollToNextStep(true);
          }
        })}
        onInputChange={onInputChange}
        placeholder={'Select a Team Member...'}
        hasValidationError={assignee == null}
      />
      <ActionButton
        disabled={assignee == null}
        disableHotkey={assignee == null}
      />
    </Step>
  );
});

export default Assignee;
